'use client';

/**
 * Orquestra dados do admin a partir de User, Service e Reminder contexts.
 * Armazena localmente os retornos de getAllUsers / getByUserId (serviços e lembretes)
 * e expõe mapas + linhas enriquecidas para tabelas e modais.
 *
 * Deve ficar dentro de UserProvider, ServiceProvider e ReminderProvider.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { Reminder, Service, Subscription, User } from '@/core/domain';
import { SERVICE_TYPE_LABELS } from '@/core/domain';
import { useUser } from '@/features/app/user/presentation/context/UserContext';
import { useSubscription } from '@/features/app/subscription/presentation/context/SubscriptionContext';
import { useServices } from '@/features/app/services/presentation/context/ServiceContext';
import { useReminders } from '@/features/app/reminders/presentation/context/ReminderContext';

// ---------------------------------------------------------------------------
// Planos: preencha quando houver fonte (ex. Firestore). Senão, label sintético.
// ---------------------------------------------------------------------------

const PLAN_NAME_BY_ID: Record<string, string> = {};

function planLabel(planId: string | null): string {
  if (!planId) return 'Sem plano';
  return PLAN_NAME_BY_ID[planId] ?? `Plano (${planId.slice(0, 8)}…)`;
}

function userDisplayName(user: User | undefined): string {
  if (!user) return '—';
  const n = user.name?.trim();
  if (n) return n;
  const c = user.company_name?.trim();
  if (c) return c;
  return '—';
}

function serviceTypeLabel(s: Service): string {
  const base = SERVICE_TYPE_LABELS[s.type];
  if (s.type === 'outro' && s.type_other_description) {
    return `${base}: ${s.type_other_description}`;
  }
  return base;
}

// ---------------------------------------------------------------------------
// DTOs (apresentação admin)
// ---------------------------------------------------------------------------

export interface AdminUserRow extends User {
  /** Nome para exibição na tabela (pessoa ou empresa). */
  displayName: string;
  /** Texto do plano para célula clicável / modal. */
  planDisplayLabel: string;
}

export interface AdminServiceRow extends Service {
  ownerDisplayName: string;
  /** Placeholder até haver join com Client (sem exibir client_id na UI). */
  clientDisplayLabel: string;
  serviceTypeLabel: string;
}

export interface AdminReminderRow extends Reminder {
  userDisplayName: string;
  clientDisplayLabel: string;
  /** Resumo do serviço vinculado ou fallback do id. */
  serviceDisplayLabel: string;
}

// ---------------------------------------------------------------------------

export interface AdminAllDataState {
  users: User[];
  services: Service[];
  reminders: Reminder[];
  usersLoading: boolean;
  servicesLoading: boolean;
  remindersLoading: boolean;
  usersError: string | null;
  servicesError: string | null;
  remindersError: string | null;
  enrichedUsers: AdminUserRow[];
  enrichedServices: AdminServiceRow[];
  enrichedReminders: AdminReminderRow[];
  usersById: ReadonlyMap<string, User>;
  servicesById: ReadonlyMap<string, Service>;
}

export interface AdminAllDataEvents {
  refreshUsers: (options?: { forceRefresh?: boolean }) => Promise<void>;
  refreshServices: (options?: { forceRefresh?: boolean }) => Promise<void>;
  refreshReminders: (options?: { forceRefresh?: boolean }) => Promise<void>;
  refreshAll: (options?: { forceRefresh?: boolean }) => Promise<void>;
  clearErrors: () => void;
}

export interface AdminAllDataContextValue {
  state: AdminAllDataState;
  events: AdminAllDataEvents;
}

const AdminAllDataContext = createContext<AdminAllDataContextValue | null>(null);

export function AdminAllDataProvider({ children }: { children: React.ReactNode }) {
  const { events: userEvents } = useUser();
  const { events: subscriptionEvents } = useSubscription();
  const { events: serviceEvents } = useServices();
  const { events: reminderEvents } = useReminders();

  const [users, setUsers] = useState<User[]>([]);
  const [subscriptionsByUserId, setSubscriptionsByUserId] = useState<
    Map<string, Subscription | null>
  >(() => new Map());
  const [services, setServices] = useState<Service[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);

  const [usersLoading, setUsersLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [remindersLoading, setRemindersLoading] = useState(false);

  const [usersError, setUsersError] = useState<string | null>(null);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [remindersError, setRemindersError] = useState<string | null>(null);

  const refreshUsers = useCallback(
    async (options?: { forceRefresh?: boolean }) => {
      setUsersLoading(true);
      setUsersError(null);
      try {
        const list = await userEvents.getAllUsers(options);
        const subs = await subscriptionEvents.getSubscriptionsByUserIds(list.map((u) => u.id));
        setUsers(list);
        setSubscriptionsByUserId(subs);
      } catch (e) {
        setUsersError(e instanceof Error ? e.message : String(e));
        setUsers([]);
        setSubscriptionsByUserId(new Map());
      } finally {
        setUsersLoading(false);
      }
    },
    [userEvents, subscriptionEvents]
  );

  const refreshServices = useCallback(
    async (options?: { forceRefresh?: boolean }) => {
      setServicesLoading(true);
      setServicesError(null);
      try {
        const list = await serviceEvents.getByUserId(options);
        setServices(list);
      } catch (e) {
        setServicesError(e instanceof Error ? e.message : String(e));
        setServices([]);
      } finally {
        setServicesLoading(false);
      }
    },
    [serviceEvents]
  );

  const refreshReminders = useCallback(
    async (options?: { forceRefresh?: boolean }) => {
      setRemindersLoading(true);
      setRemindersError(null);
      try {
        const list = await reminderEvents.getByUserId(options);
        setReminders(list);
      } catch (e) {
        setRemindersError(e instanceof Error ? e.message : String(e));
        setReminders([]);
      } finally {
        setRemindersLoading(false);
      }
    },
    [reminderEvents]
  );

  const refreshAll = useCallback(
    async (options?: { forceRefresh?: boolean }) => {
      await Promise.all([
        refreshUsers(options),
        refreshServices(options),
        refreshReminders(options),
      ]);
    },
    [refreshUsers, refreshServices, refreshReminders]
  );

  const clearErrors = useCallback(() => {
    setUsersError(null);
    setServicesError(null);
    setRemindersError(null);
  }, []);

  const { usersById, servicesById } = useMemo(() => {
    const ub = new Map<string, User>();
    for (const u of users) ub.set(u.id, u);
    const sb = new Map<string, Service>();
    for (const s of services) sb.set(s.id, s);
    return { usersById: ub, servicesById: sb };
  }, [users, services]);

  const enrichedUsers = useMemo<AdminUserRow[]>(
    () =>
      users.map((u) => ({
        ...u,
        displayName: userDisplayName(u),
        planDisplayLabel: planLabel(subscriptionsByUserId.get(u.id)?.plan_id ?? null),
      })),
    [users, subscriptionsByUserId]
  );

  const enrichedServices = useMemo<AdminServiceRow[]>(
    () =>
      services.map((s) => {
        const owner = usersById.get(s.user_id);
        return {
          ...s,
          ownerDisplayName: userDisplayName(owner),
          clientDisplayLabel: 'Cliente',
          serviceTypeLabel: serviceTypeLabel(s),
        };
      }),
    [services, usersById]
  );

  const enrichedReminders = useMemo<AdminReminderRow[]>(
    () =>
      reminders.map((r) => {
        const u = usersById.get(r.user_id);
        const svc = r.service_id ? servicesById.get(r.service_id) : undefined;
        let serviceDisplayLabel = '—';
        if (svc) {
          serviceDisplayLabel = `${serviceTypeLabel(svc)} · ${svc.service_date}`;
        } else if (r.service_id) {
          serviceDisplayLabel = 'Serviço';
        }
        return {
          ...r,
          userDisplayName: userDisplayName(u),
          clientDisplayLabel: 'Cliente',
          serviceDisplayLabel,
        };
      }),
    [reminders, usersById, servicesById]
  );

  const state = useMemo<AdminAllDataState>(
    () => ({
      users,
      services,
      reminders,
      usersLoading,
      servicesLoading,
      remindersLoading,
      usersError,
      servicesError,
      remindersError,
      enrichedUsers,
      enrichedServices,
      enrichedReminders,
      usersById,
      servicesById,
    }),
    [
      users,
      services,
      reminders,
      usersLoading,
      servicesLoading,
      remindersLoading,
      usersError,
      servicesError,
      remindersError,
      enrichedUsers,
      enrichedServices,
      enrichedReminders,
      usersById,
      servicesById,
    ]
  );

  const events = useMemo<AdminAllDataEvents>(
    () => ({
      refreshUsers,
      refreshServices,
      refreshReminders,
      refreshAll,
      clearErrors,
    }),
    [refreshUsers, refreshServices, refreshReminders, refreshAll, clearErrors]
  );

  const value = useMemo<AdminAllDataContextValue>(
    () => ({ state, events }),
    [state, events]
  );

  return (
    <AdminAllDataContext.Provider value={value}>{children}</AdminAllDataContext.Provider>
  );
}

export function useAdminAllData(): AdminAllDataContextValue {
  const ctx = useContext(AdminAllDataContext);
  if (!ctx) {
    throw new Error('useAdminAllData must be used within AdminAllDataProvider');
  }
  return ctx;
}
